import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import * as Sentry from "@sentry/react-native";
import { useRouter } from "expo-router";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackTitle?: string;
}

function ErrorFallback({
  title,
  onRetry,
}: {
  title: string;
  onRetry: () => void;
}) {
  return (
    <View style={styles.container}>
      <ThemedText style={styles.title} type="subtitle">
        {title}
      </ThemedText>
      <ThemedText style={styles.message} type="small">
        Something went wrong. Please try again.
      </ThemedText>
      <TouchableOpacity
        style={styles.button}
        onPress={onRetry}
        accessibilityRole="button"
        accessibilityLabel="Try again"
      >
        <ThemedText style={styles.buttonText} type="defaultSemiBold">
          Try again
        </ThemedText>
      </TouchableOpacity>
    </View>
  );
}

export function AppErrorBoundary({
  children,
  fallbackTitle = "Unable to load this screen",
}: ErrorBoundaryProps) {
  const router = useRouter();

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <ErrorFallback
          title={fallbackTitle}
          onRetry={() => {
            resetError();
            router.replace("/(tabs)");
          }}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: BrandColors.white,
  },
  title: {
    marginBottom: 8,
    textAlign: "center",
  },
  message: {
    marginBottom: 24,
    textAlign: "center",
    color: BrandColors.mediumGray,
  },
  button: {
    backgroundColor: BrandColors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    color: BrandColors.white,
  },
});
